import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, gridActionButton, getODataRows, resolveODataTotalCount } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/orderheaders';

const EMPTY_FORM = {
  customerId: '', representativeId: '', vendorId: '', warehouseId: '',
  documentDate: '', documentType: '', status: '', confirmStatus: '',
  discountAmount: '', grossAmount: '', netAmount: '', specialConsumptionTaxAmount: '', vatAmount: '', vatBase: '',
  orderDescription: '', paymentType: '', shippingAddress: '', shippingType: '',
};

const LOOKUP_SORT_FIELDS = ['CustomerLabel', 'RepresentativeLabel', 'VendorLabel', 'WarehouseLabel'];

const orderEditorStyles = {
  shell: {
    width: 'min(1280px, calc(100vw - 36px))',
    maxHeight: '92vh',
    overflow: 'auto',
    background: '#f3f6f8',
    borderRadius: 8,
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.32)',
    color: '#111827',
  },
  header: {
    margin: 18,
    marginBottom: 14,
    padding: 20,
    borderRadius: 8,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  eyebrow: {
    margin: 0,
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 700,
  },
  title: {
    margin: '4px 0 0',
    color: '#111827',
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: 0,
  },
  subtitle: {
    margin: '8px 0 0',
    color: '#4b5563',
    fontSize: 13,
    maxWidth: 620,
  },
  headerActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  body: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(300px, 0.9fr)',
    gap: 18,
    padding: '0 18px 18px',
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    minWidth: 0,
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    minWidth: 0,
  },
  section: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 18,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 14,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    color: '#111827',
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 14,
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 800,
  },
  input: {
    ...s.input,
    borderRadius: 8,
    background: '#f9fafb',
    minHeight: 42,
  },
  inputView: {
    ...s.inputView,
    borderRadius: 8,
    minHeight: 42,
  },
  select: {
    ...s.select,
    borderRadius: 8,
    background: '#f9fafb',
    minHeight: 42,
  },
  textarea: {
    ...s.input,
    borderRadius: 8,
    background: '#f9fafb',
    minHeight: 86,
    resize: 'vertical',
  },
  lineTable: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
  },
  lineHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 0.8fr 1fr 1fr',
    gap: 8,
    padding: '10px 12px',
    background: '#f9fafb',
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  lineRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 0.8fr 1fr 1fr',
    gap: 8,
    padding: '12px',
    borderTop: '1px solid #e5e7eb',
    color: '#111827',
    fontSize: 13,
    alignItems: 'center',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 0',
    color: '#4b5563',
    fontSize: 13,
  },
  summaryStrong: {
    borderTop: '1px solid #e5e7eb',
    marginTop: 6,
    paddingTop: 14,
    color: '#111827',
    fontSize: 18,
    fontWeight: 900,
  },
  notePanel: {
    background: 'linear-gradient(135deg, #2952e3 0%, #345eea 100%)',
    color: '#ffffff',
    borderRadius: 8,
    padding: '24px 22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
  },
  notePanelInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  notePanelEyebrow: {
    margin: 0,
    color: 'rgba(255,255,255,0.84)',
    fontSize: 13,
    fontWeight: 600,
  },
  notePanelTitle: {
    margin: 0,
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1.15,
  },
  notePanelMeta: {
    margin: 0,
    color: 'rgba(255,255,255,0.82)',
    fontSize: 13,
  },
  notePanelBadge: {
    flexShrink: 0,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.12)',
    color: '#ffffff',
    padding: '10px 16px',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
};

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function toRequiredInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function toNullableShort(value) {
  if (value === '' || value == null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function toRequiredDecimal(value) {
  const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function toDateTimeOffset(value) {
  return value ? new Date(value).toISOString() : new Date().toISOString();
}

function dateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function compareText(a, b, direction) {
  const left = (a || '').toString().toLocaleLowerCase('tr-TR');
  const right = (b || '').toString().toLocaleLowerCase('tr-TR');
  const result = left.localeCompare(right, 'tr');
  return direction === 'asc' ? result : -result;
}

const moneyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatMoney(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? moneyFormatter.format(parsed) : '-';
}

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

function getOrderStatusSummary(status, confirmStatus) {
  switch (toRequiredInt(status)) {
    case 1:
      return { label: 'Hazirlaniyor', badge: 'Onaylandi' };
    case 2:
      return { label: 'Onaylandi', badge: 'Hazir' };
    case 3:
      return { label: 'Sevk Edildi', badge: 'Yolda' };
    case 4:
      return { label: 'Tamamlandi', badge: 'Kapanis' };
    case 5:
      return { label: 'Iptal', badge: 'Pasif' };
    default:
      if (toNullableShort(confirmStatus) === 1) return { label: 'Planlaniyor', badge: 'Onaylandi' };
      return { label: 'Beklemede', badge: 'Onay Bekliyor' };
  }
}

export function OrderHeadersPageLegacy() {
  usePageMeta('Sales Operations', 'Sipariş başlıkları · Filter + sort · Demo');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);

  const [sortField, setSortField] = useState('DocumentDate');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [amountDetailsOpen, setAmountDetailsOpen] = useState(false);

  const labelBy = useCallback((items, id, fields) => {
    const item = items.find(x => x.id === id);
    if (!item) return '-';
    const code = item.textId || item.erpId || '';
    const name = fields.map(field => item[field]).find(Boolean) || '';
    return name ? `${code} - ${name}`.trim().replace(/^- /, '') : code || '-';
  }, []);

  const getCustomerLabel = useCallback((id) => labelBy(customers, id, ['title', 'shortName', 'person']), [customers, labelBy]);
  const getRepresentativeLabel = useCallback((id) => labelBy(representatives, id, ['name']), [representatives, labelBy]);
  const getVendorLabel = useCallback((id) => labelBy(vendors, id, ['name']), [vendors, labelBy]);
  const getWarehouseLabel = useCallback((id) => labelBy(warehouses, id, ['name', 'description']), [warehouses, labelBy]);
  const getProductLabel = useCallback((id) => labelBy(products, id, ['name', 'shortName', 'description']), [products, labelBy]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customers/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/representatives/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/vendors/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/warehouses/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/products/Select', { headers }).then(res => res.ok ? res.json() : []),
    ]).then(([customerData, representativeData, vendorData, warehouseData, productData]) => {
      setCustomers(unwrapLookupItems(customerData));
      setRepresentatives(unwrapLookupItems(representativeData));
      setVendors(unwrapLookupItems(vendorData));
      setWarehouses(unwrapLookupItems(warehouseData));
      setProducts(unwrapLookupItems(productData));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        filterParams = `(contains(tolower(OrderDescription),'${q}') or contains(tolower(ShippingType),'${q}') or contains(tolower(ShippingAddress),'${q}'))`;
      }

      const isLookupSort = LOOKUP_SORT_FIELDS.includes(sortField);
      const url = buildODataUrl(API_BASE, {
        pageSize: isLookupSort ? 100000 : pageSize,
        currentPage: isLookupSort ? 1 : currentPage,
        sortField: isLookupSort ? 'DocumentDate' : sortField,
        sortDir,
        filterParams,
      });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alinamadi');
      const data = await res.json();
      const allRows = getODataRows(data);

      if (isLookupSort) {
        const skip = (currentPage - 1) * pageSize;
        const sortedRows = [...allRows].sort((a, b) => {
          const labels = {
            CustomerLabel: [getCustomerLabel(a.customerId), getCustomerLabel(b.customerId)],
            RepresentativeLabel: [getRepresentativeLabel(a.representativeId), getRepresentativeLabel(b.representativeId)],
            VendorLabel: [getVendorLabel(a.vendorId), getVendorLabel(b.vendorId)],
            WarehouseLabel: [getWarehouseLabel(a.warehouseId), getWarehouseLabel(b.warehouseId)],
          };
          return compareText(labels[sortField][0], labels[sortField][1], sortDir);
        });
        setRows(sortedRows.slice(skip, skip + pageSize));
        setTotalCount(sortedRows.length);
      } else {
        setRows(allRows);
        setTotalCount(await resolveODataTotalCount({ data, rows: allRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders() }));
      }
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [filterText, sortField, sortDir, pageSize, currentPage, getCustomerLabel, getRepresentativeLabel, getVendorLabel, getWarehouseLabel]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchOrderDetails = useCallback(async (orderId) => {
    if (!orderId) return [];
    // #795 Kalem sırası LineOrder'dadır (#757/#758); UnitOrder birim slotudur.
    const url = `/api/orderdetails?$filter=${encodeURIComponent(`OrderId eq ${orderId}`)}&$orderby=${encodeURIComponent('LineOrder asc,Id asc')}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) throw new Error('Siparis kalemleri alinamadi');
    return getODataRows(await res.json());
  }, []);

  const rowToForm = (row) => ({
    customerId: row.customerId || '',
    representativeId: row.representativeId || '',
    vendorId: row.vendorId || '',
    warehouseId: row.warehouseId || '',
    documentDate: dateInputValue(row.documentDate),
    documentType: row.documentType ?? '',
    status: row.status ?? '',
    confirmStatus: row.confirmStatus ?? '',
    discountAmount: row.discountAmount ?? '',
    grossAmount: row.grossAmount ?? '',
    netAmount: row.netAmount ?? '',
    specialConsumptionTaxAmount: row.specialConsumptionTaxAmount ?? '',
    vatAmount: row.vatAmount ?? '',
    vatBase: row.vatBase ?? '',
    orderDescription: row.orderDescription || '',
    paymentType: row.paymentType || '',
    shippingAddress: row.shippingAddress || '',
    shippingType: row.shippingType || '',
  });

  const openNew = () => {
    setForm({ ...EMPTY_FORM, documentDate: dateInputValue(new Date()) });
    setOrderDetails([]);
    setAmountDetailsOpen(false);
    setSelected(null);
    setModal('new');
  };

  const openEdit = async (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setAmountDetailsOpen(false);
    setModal('edit');
    try {
      setOrderDetails(await fetchOrderDetails(row.id));
    } catch (error) {
      showToast('error', error.message || 'Siparis kalemleri alinamadi');
    }
  };

  const openView = async (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setAmountDetailsOpen(false);
    setModal('view');
    try {
      setOrderDetails(await fetchOrderDetails(row.id));
    } catch (error) {
      showToast('error', error.message || 'Siparis kalemleri alinamadi');
    }
  };

  const closeModal = () => {
    setModal(null);
    setOrderDetails([]);
    setAmountDetailsOpen(false);
  };

  const buildPayload = () => ({
    customerId: form.customerId,
    representativeId: form.representativeId,
    vendorId: form.vendorId,
    warehouseId: form.warehouseId,
    documentDate: toDateTimeOffset(form.documentDate),
    documentType: toRequiredInt(form.documentType),
    status: toRequiredInt(form.status),
    confirmStatus: toNullableShort(form.confirmStatus),
    discountAmount: toRequiredDecimal(form.discountAmount),
    grossAmount: toRequiredDecimal(form.grossAmount),
    netAmount: toRequiredDecimal(form.netAmount),
    specialConsumptionTaxAmount: toRequiredDecimal(form.specialConsumptionTaxAmount),
    vatAmount: toRequiredDecimal(form.vatAmount),
    vatBase: toRequiredDecimal(form.vatBase),
    orderDescription: form.orderDescription || null,
    paymentType: form.paymentType || null,
    shippingAddress: form.shippingAddress || null,
    shippingType: form.shippingType || null,
  });

  const handleSave = async () => {
    if (!form.customerId || !form.representativeId || !form.vendorId || !form.warehouseId) {
      showToast('error', 'Musteri, temsilci, tedarikci ve depo secimi zorunludur');
      return;
    }

    try {
      const isNew = modal === 'new';
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id: selected.id, ...payload }),
      });
      if (!res.ok) throw new Error(isNew ? 'Kayit eklenemedi' : 'Kayit guncellenemedi');
      showToast('success', isNew ? 'Siparis basariyla eklendi' : 'Siparis basariyla guncellendi');
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Islem sirasinda hata olustu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      if (!res.ok) throw new Error('Kayit silinemedi');
      showToast('success', 'Siparis basariyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const isView = modal === 'view';
  const selectStyle = isView ? orderEditorStyles.inputView : orderEditorStyles.select;
  const orderStatus = getOrderStatusSummary(form.status, form.confirmStatus);
  const detailTotals = orderDetails.reduce((acc, row) => ({
    grossAmount: acc.grossAmount + Number(row.grossAmount || 0),
    netAmount: acc.netAmount + Number(row.netAmount || 0),
    discountAmount: acc.discountAmount + Number(row.discountAmount || 0),
    vatAmount: acc.vatAmount + Number(row.vatAmount || 0),
  }), { grossAmount: 0, netAmount: 0, discountAmount: 0, vatAmount: 0 });
  const effectiveTotals = orderDetails.length > 0
    ? {
        grossAmount: detailTotals.grossAmount,
        netAmount: detailTotals.netAmount,
        discountAmount: detailTotals.discountAmount,
        vatAmount: detailTotals.vatAmount,
        vatBase: Number(form.vatBase || 0),
        specialConsumptionTaxAmount: Number(form.specialConsumptionTaxAmount || 0),
      }
    : {
        grossAmount: Number(form.grossAmount || 0),
        netAmount: Number(form.netAmount || 0),
        discountAmount: Number(form.discountAmount || 0),
        vatAmount: Number(form.vatAmount || 0),
        vatBase: Number(form.vatBase || 0),
        specialConsumptionTaxAmount: Number(form.specialConsumptionTaxAmount || 0),
      };

  const columns = [
    {
      field: 'documentDate',
      headerName: 'Tarih',
      width: 130,
      renderCell: (params) => params.row.documentDate ? new Date(params.row.documentDate).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'customerLabel',
      headerName: 'Musteri',
      flex: 1.2,
      minWidth: 190,
      sortable: false,
      renderCell: (params) => getCustomerLabel(params.row.customerId),
    },
    {
      field: 'representativeLabel',
      headerName: 'Temsilci',
      flex: 1.1,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => getRepresentativeLabel(params.row.representativeId),
    },
    {
      field: 'vendorLabel',
      headerName: 'Tedarikci',
      flex: 1.1,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => getVendorLabel(params.row.vendorId),
    },
    {
      field: 'warehouseLabel',
      headerName: 'Depo',
      flex: 1.1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => getWarehouseLabel(params.row.warehouseId),
    },
    {
      field: 'netAmount',
      headerName: 'Net',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <div style={{ width: '100%', textAlign: 'right' }}>
          {formatMoney(params.row.netAmount)}
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: 'Islemler',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button
            style={{ ...gridActionButton, background: 'var(--accent)' }}
            onClick={(event) => { event.stopPropagation(); openView(params.row); }}
          >
            Goruntule
          </button>
          <button
            style={{ ...gridActionButton, background: 'var(--warn)' }}
            onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}
          >
            Degistir
          </button>
          <button
            style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }}
            onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}
          >
            Sil
          </button>
        </div>
      ),
    },
  ];

  const selectField = (label, key, items, getLabel) => (
    <div style={orderEditorStyles.field}>
      <label style={orderEditorStyles.label}>{label}</label>
      {isView ? (
        <input style={orderEditorStyles.inputView} readOnly value={getLabel(form[key])} />
      ) : (
        <select style={selectStyle} value={form[key]} onChange={e => setForm(current => ({ ...current, [key]: e.target.value }))}>
          <option value="">Seciniz</option>
          {items.map(item => <option key={item.id} value={item.id}>{getLabel(item.id)}</option>)}
        </select>
      )}
    </div>
  );

  const editorField = (label, key, type = 'text', options = {}) => {
    const style = { ...(isView ? orderEditorStyles.inputView : orderEditorStyles.input), textAlign: options.align || 'left' };
    return (
      <div style={orderEditorStyles.field}>
        <label style={orderEditorStyles.label}>{label}</label>
        {options.multiline ? (
          <textarea style={orderEditorStyles.textarea} value={form[key] ?? ''} onChange={e => setForm(current => ({ ...current, [key]: e.target.value }))} readOnly={isView} />
        ) : (
          <input type={type} style={style} value={form[key] ?? ''} onChange={e => setForm(current => ({ ...current, [key]: e.target.value }))} readOnly={isView} />
        )}
      </div>
    );
  };

  const summaryRow = (label, value, strong = false) => (
    <div style={strong ? { ...orderEditorStyles.summaryRow, ...orderEditorStyles.summaryStrong } : orderEditorStyles.summaryRow}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Siparisler</h2>
          <p style={s.pageSubtitle}>Siparisleri filtreleyin, siralayin ve yonetin.</p>
        </div>
        <button style={s.btnPrimary} onClick={openNew}>+ Yeni Siparis</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Aciklama veya sevkiyat ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') { setFilterText(filterInput); setCurrentPage(1); } }}
          />
          {filterText && <button style={s.btnSecondary} onClick={() => { setFilterText(''); setFilterInput(''); setCurrentPage(1); }}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => {
          setCurrentPage(1);
          setSortField(field || 'DocumentDate');
          setSortDir(direction || 'asc');
        }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
        exportFileName="siparisler"
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={orderEditorStyles.shell} onClick={e => e.stopPropagation()}>
            <div style={orderEditorStyles.header}>
              <div>
                <p style={orderEditorStyles.eyebrow}>Siparis Yonetimi</p>
                <h3 style={orderEditorStyles.title}>{modal === 'new' ? 'Yeni Siparis' : modal === 'edit' ? 'Siparis Duzenle' : 'Siparis Detay'}</h3>
                <p style={orderEditorStyles.subtitle}>Musteri, operasyon ve siparis tutarlarini tek ekranda fatura benzeri duzende inceleyin.</p>
              </div>
              <div style={orderEditorStyles.headerActions}>
                {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
                <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'Iptal'}</button>
              </div>
            </div>

            <div style={orderEditorStyles.body}>
              <div style={orderEditorStyles.mainColumn}>
                <section style={orderEditorStyles.section}>
                  <div style={orderEditorStyles.sectionHeader}>
                    <h4 style={orderEditorStyles.sectionTitle}>Siparis Bilgileri</h4>
                  </div>
                  <div style={orderEditorStyles.grid3}>
                    {editorField('Siparis Tarihi', 'documentDate', 'date')}
                    {editorField('Evrak Tipi', 'documentType')}
                    {editorField('Durum Kodu', 'status')}
                    {editorField('Onay Durumu', 'confirmStatus')}
                  </div>
                </section>

                <section style={orderEditorStyles.section}>
                  <h4 style={{ ...orderEditorStyles.sectionTitle, marginBottom: 14 }}>Musteri ve Operasyon Bilgileri</h4>
                  <div style={orderEditorStyles.grid2}>
                    {selectField('Musteri *', 'customerId', customers, getCustomerLabel)}
                    {selectField('Temsilci *', 'representativeId', representatives, getRepresentativeLabel)}
                    {selectField('Tedarikci *', 'vendorId', vendors, getVendorLabel)}
                    {selectField('Depo *', 'warehouseId', warehouses, getWarehouseLabel)}
                  </div>
                </section>

                <section style={orderEditorStyles.section}>
                  <div style={orderEditorStyles.sectionHeader}>
                    <h4 style={orderEditorStyles.sectionTitle}>Siparis Kalemleri</h4>
                  </div>
                  <div style={orderEditorStyles.lineTable}>
                    <div style={orderEditorStyles.lineHeader}>
                      <div>Urun</div>
                      <div style={{ textAlign: 'right' }}>Miktar</div>
                      <div style={{ textAlign: 'right' }}>Birim Fiyat</div>
                      <div style={{ textAlign: 'right' }}>Net</div>
                    </div>
                    {orderDetails.length === 0 ? (
                      <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Kalem bulunamadi.</div>
                    ) : orderDetails.map(row => (
                      <div key={row.id} style={orderEditorStyles.lineRow}>
                        <div>{getProductLabel(row.productId)}</div>
                        <div style={{ textAlign: 'right' }}>{row.quantity}</div>
                        <div style={{ textAlign: 'right' }}>{formatMoney(row.unitPrice)}</div>
                        <div style={{ textAlign: 'right', fontWeight: 800 }}>{formatMoney(row.netAmount)}</div>
                      </div>
                    ))}
                  </div>
                </section>

                <section style={orderEditorStyles.section}>
                  <h4 style={{ ...orderEditorStyles.sectionTitle, marginBottom: 14 }}>Sevkiyat ve Aciklama</h4>
                  <div style={orderEditorStyles.grid2}>
                    {editorField('Sevkiyat Tipi', 'shippingType')}
                    {editorField('Sevkiyat Adresi', 'shippingAddress', 'text', { multiline: true })}
                  </div>
                  <div style={{ ...orderEditorStyles.grid2, marginTop: 14 }}>
                    {editorField('Aciklama', 'orderDescription', 'text', { multiline: true })}
                  </div>
                </section>
              </div>

              <aside style={orderEditorStyles.sideColumn}>
                <section style={orderEditorStyles.section}>
                  <h4 style={{ ...orderEditorStyles.sectionTitle, marginBottom: 14 }}>Ozet</h4>
                  {summaryRow('Ara Toplam', formatMoney(effectiveTotals.grossAmount))}
                  {summaryRow('Iskonto', formatMoney(effectiveTotals.discountAmount))}
                  {summaryRow('KDV Toplami', formatMoney(effectiveTotals.vatAmount))}
                  {summaryRow('OTV', formatMoney(effectiveTotals.specialConsumptionTaxAmount))}
                  {summaryRow('Genel Toplam', formatMoney(effectiveTotals.netAmount), true)}
                </section>

                <section style={orderEditorStyles.notePanel}>
                  <div style={orderEditorStyles.notePanelInfo}>
                    <p style={orderEditorStyles.notePanelEyebrow}>Siparis Durumu</p>
                    <h4 style={orderEditorStyles.notePanelTitle}>{orderStatus.label}</h4>
                    <p style={orderEditorStyles.notePanelMeta}>Siparis tarihi: {displayDate(form.documentDate)}</p>
                  </div>
                  <span style={orderEditorStyles.notePanelBadge}>{orderStatus.badge}</span>
                </section>

                <section style={orderEditorStyles.section}>
                  <div style={{ ...orderEditorStyles.sectionHeader, marginBottom: amountDetailsOpen ? 14 : 0 }}>
                    <h4 style={orderEditorStyles.sectionTitle}>Tutar Detaylari</h4>
                    <button type="button" style={{ ...s.btnSecondary, minWidth: 36, padding: '7px 10px' }} onClick={() => setAmountDetailsOpen(value => !value)} title={amountDetailsOpen ? 'Kapat' : 'Ac'}>
                      {amountDetailsOpen ? '-' : '+'}
                    </button>
                  </div>
                  {amountDetailsOpen && (
                    <div style={orderEditorStyles.grid2}>
                      {editorField('Brut Tutar', 'grossAmount', 'text', { align: 'right' })}
                      {editorField('Net Tutar', 'netAmount', 'text', { align: 'right' })}
                      {editorField('Iskonto Tutar', 'discountAmount', 'text', { align: 'right' })}
                      {editorField('KDV Matrah', 'vatBase', 'text', { align: 'right' })}
                      {editorField('KDV Tutar', 'vatAmount', 'text', { align: 'right' })}
                      {editorField('OTV Tutar', 'specialConsumptionTaxAmount', 'text', { align: 'right' })}
                    </div>
                  )}
                </section>
              </aside>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayi</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>Bu siparisi silmek istediginize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
